import type { TranslationKeys } from './keys';

export const en = {
  // App title and description
  appTitle: "Travel Diary",
  appDescription: "Save and plan your travels",
  
  // Tab names
  myFlights: "Flights",
  carTrips: "Car",
  transport: "Transport",
  trips: "Trips",
  buses: "Bus",
  trains: "Train",
  ferries: "Ferry",
  addBusTrip: "Add bus trip",
  addTrainTrip: "Add train trip",
  addFerryTrip: "Add ferry trip",
  editBusTrip: "Edit bus trip",
  editTrainTrip: "Edit train trip",
  editFerryTrip: "Edit ferry trip",
  busOperator: "Operator",
  trainOperator: "Operator",
  ferryOperator: "Operator",
  busNumber: "Route number",
  trainNumber: "Train number",
  ferryNumber: "Sailing number",
  notes: "Notes",
  analytics: "Analytics",
  statistics: "Statistics",
  routeMap: "Map",
  planning: "Plan",
  journeys: "Diary",
  
  // Authentication
  login: "Login",
  loginSubtitle: "Please enter your details to sign in",
  register: "Register",
  registerSubtitle: "Please enter your details to create an account",
  logout: "Logout",
  email: "Email",
  password: "Password",
  confirmPassword: "Confirm Password",
  loginWithEmail: "Login with Email",
  loginWithGoogle: "Sign in with Google",
  createAccount: "Create Account",
  orContinueWith: "Or continue with",
  dontHaveAccount: "Don't have an account?",
  alreadyHaveAccount: "Already have an account?",
  forgotPassword: "Forgot password?",
  forgotPasswordTitle: "Password recovery",
  forgotPasswordSent: "If an account with this email exists, we've sent a link to reset your password.",
  resetPassword: "Reset password",
  confirmEmailTitle: "Email confirmation",
  confirmEmailSent: "We've sent a confirmation link to your email.",
  passwordsDoNotMatch: "Passwords do not match",
  passwordTooShort: "Password must be at least 6 characters",
  resetPasswordSubtitle: "Enter your new password below.",
  resetPasswordSuccess: "Password reset successfully. Redirecting to login…",
  resetPasswordInvalidLink: "Invalid or missing reset link. Please request a new one.",
  missingResetToken: "Missing reset token",
  resetFailed: "Reset failed",
  backToApp: "Back to app",
  goToApp: "Go to app",
  forgotPasswordHint: "Enter your email and we'll send a reset link.",
  requestFailed: "Request failed",
  confirmingEmail: "Confirming your email…",
  confirmEmailSuccessSubtitle: "You can close this page and sign in.",
  confirmEmailError: "Error",
  error: "Error",
  loading: "Loading...",
  
  // Card titles
  totalFlights: "Total Flights",
  totalDistance: "Total Distance",
  averagePrice: "Average Price",
  totalSpent: "Total Spent",
  totalTrips: "Total Trips",
  totalExpenses: "Total Expenses",
  fuelConsumption: "Fuel Consumption",
  fuelExpenses: "Fuel Expenses",
  roadExpenses: "Road Expenses",
  visitedCities: "Visited Cities",
  
  // Units
  km: "km",
  milesUnit: "Miles",
  liters: "l",
  litersPerHundredKm: "l/100km",
  roadNumbers: "Road Numbers",
  hours: "hrs",
  
  // Status messages
  calculatingDistance: "Calculating distance...",
  
  // Analytics / Statistics titles
  flightsByMonth: "Flights by Month",
  flightsPerYear: "Flights per Year",
  flightsPerWeekday: "Flights per Weekday",
  yearsOfFlying: "years of flying",
  chartCount: "Count",
  carTripsByWeekday: "Trips by weekday",
  carTripsPerYear: "Trips per year",
  tripsByMonth: "Trips by month",
  topCities: "Top cities",
  topOperators: "Top operators",
  totalCities: "total cities",
  totalOperators: "total operators",
  yearsOnTheRoad: "years on the road",
  totalTimeOnRoad: "Time on the road",
  totalTimeInFlight: "Time in flight",
  daysUnit: "days",
  monthsUnit: "mo",
  topAirports: "Top Airports",
  topAirlines: "Top Airlines",
  topAircraft: "Top Aircraft",
  topRoutes: "Top Routes",
  topCountries: "Top Countries",
  totalAirports: "total airports",
  totalAirlines: "total airlines",
  totalAircraft: "total aircraft types",
  totalRoutes: "total routes",
  totalCountries: "total countries",
  flightsCount: "flights",
  domesticCount: "domestic",
  internationalCount: "international",
  airlines: "Airlines",
  flightClasses: "Class",
  carsByYear: "Cars by Year",
  carBrands: "Car Brands",
  fuelTypes: "Fuel Types",
  mileageByMonth: "Mileage by Month (km)",
  fuelConsumptionByCarBrand: "Fuel Consumption by Car Brand (l)",
  fuelExpensesByMonth: "Fuel Expenses by Month ($)",
  statsPeriodAll: "All time",
  statsPeriodYear: "This year",
  statsPeriodRolling12: "Last 12 months",
  statsPeriodCustom: "Custom",
  statsPeriodFrom: "From",
  statsPeriodTo: "To",
  statsTagAll: "All trips",
  statsTagFilter: "Trip tag",
  statsCountries: "Countries",
  statsGeneral: "Overview",
  statsFunFacts: "Fun facts",
  statsFunFactsDistance: "Distance",
  statsFunFactsCost: "Cost",
  statsFunFactsTime: "Time",
  statsSpendByMode: "Spend by category",
  statsDistanceByMode: "Distance by mode",
  statsByCategory: "Breakdown by category",
  visitedCountriesTitle: "Visited countries",
  visitedCountriesCount: "Countries marked",
  visitedCountriesCountUn: "UN member states",
  visitedCountriesSync: "Update from trips",
  visitedCountriesHint: "Countries sync automatically from your trips. Map color intensity reflects visited regions within each country.",
  visitedCountriesResidence: "Country of residence",
  visitedCountriesPercent: "Of UN member states",
  visitedCountriesFreelyAssociated: "Freely associated states",
  visitedCountriesKingdomTerritories: "Territories and countries within kingdoms",
  visitedCountriesSpecialAdministrativeRegions: "Special administrative regions",
  visitedCountriesDisputedTerritories: "Disputed territories",
  visitedCountriesPartiallyRecognized: "Partially recognized states",
  visitedCountriesDependentTerritories: "Dependent territories",
  visitedCountriesSpecialInternationalStatus: "Territories and regions with special international legal status",
  visitedCountriesSearch: "Search country…",
  visitedCountriesSearchEmpty: "No matches",
  visitedCountriesByRegion: "Regions",
  visitedCountriesNoPlaces: "No cities recorded",
  visitedCountriesZoomIn: "Zoom in",
  visitedCountriesZoomOut: "Zoom out",
  visitedCountriesReset: "Reset zoom",
  visitedCountriesSubdivisions: "Regions / states",
  visitedCountriesSubdivisionStats: "Regions & states",
  visitedCountriesLifeExpectancy: "Life expectancy",
  visitedCountriesPopulation: "Population",
  visitedCountriesCapital: "Capital",
  visitedCountriesPeaceIndex: "Global Peace Index",
  visitedCountriesInterestingFacts: "Interesting facts",
  
  // Settings
  settings: "Settings",
  car: "Car",
  other: "Other",
  settingsPreferences: "Preferences",
  settingsGarage: "Garage",
  settingsGarageHint: "Cars for new trips and fuel estimates",
  settingsResidenceCountry: "Country of residence",
  settingsResidenceCountryHint: "Used for visa rules in trip plans (nationality / residency)",
  carYear: "Year",
  defaultCar: "Default",
  carBrand: "Car Brand",
  fuelConsumptionRate: "Fuel Consumption (l/100km)",
  fuelType: "Fuel Type",
  currency: "Currency",
  language: "Language",
  distanceUnit: "Distance Unit",
  kilometers: "Kilometers",
  addCar: "Add Car",
  deleteCar: "Delete Car",
  selectCar: "Select Car",
  theme: "Theme",
  lightTheme: "Light",
  darkTheme: "Dark",
  
  // Fuel types
  gasoline: "Gasoline",
  diesel: "Diesel",
  gas: "Gas",
  electric: "Electric",
  
  // Currencies
  ruble: "₽ (Ruble)",
  dollar: "$ (Dollar)",
  euro: "€ (Euro)",
  
  // Languages
  russian: "Русский",
  english: "English",
  
  // Buttons
  cancel: "Cancel",
  save: "Save",
  addFlight: "Add flight",
  addTrip: "Add Trip",
  newTrip: "New Trip",
  carTripDay: "Day",
  carTripDays: "Days",
  addCarTripDay: "Add day",
  carTripDaysCount: "{n} days",
  addWaypoint: "Add Waypoint",
  removeWaypoint: "Remove Waypoint",
  viewTrip: "View",
  editTrip: "Edit",
  editFlight: "Edit flight",
  deleteTrip: "Delete",
  saveChanges: "Save Changes",
  close: "Close",
  confirmDeleteTrip: "Are you sure you want to delete this trip?",
  confirmDeleteFlight: "Delete this flight?",
  borderCrossing: "Border",

  // Form labels
  date: "Date",
  dateOfDeparture: "Departure date",
  filter: "Filter",
  searchFlights: "Search by date, tag, airport, city, airline…",
  filterChipsHint: "Chips: trip tag, year or city — press Enter to add",
  filterChipsPlaceholder: "Year, tag or city…",
  resetFilter: "Reset",
  applyFilter: "Apply",
  noFilterResults: "No results found",
  filterByTag: "Tags",
  priceFrom: "Price from",
  priceTo: "to",
  dateFrom: "Date from",
  dateTo: "to",
  flightNumber: "Flight Number",
  departureCity: "Departure City",
  arrivalCity: "Arrival City",
  departureAirport: "Departure airport",
  arrivalAirport: "Arrival airport",
  airportPlaceholder: "City (code)",
  airportDescriptionUnavailable: "Description unavailable",
  departureTime: "Departure time",
  arrivalTime: "Arrival time",
  duration: "Flight duration",
  airline: "Airline",
  aircraftType: "Aircraft type",
  aircraftReg: "Aircraft reg.",
  originCity: "Origin City",
  destinationCity: "Destination City",
  originStation: "Origin station",
  destinationStation: "Destination station",
  trainDepartureTime: "Departure",
  trainArrivalTime: "Arrival",
  waypoints: "Waypoints",
  waypointCity: "Waypoint City",
  distance: "Distance (km)",
  estimatedTime: "Estimated Travel Time",
  uploadBoardingPass: "Upload boarding pass / itinerary",
  parsingBoardingPass: "Reading document…",
  boardingPassParsed: "Fields filled from document",
  boardingPassParseFailed: "Could not parse the document. Try another photo or PDF.",
  boardingPassHint: "Photo or PDF of boarding pass / itinerary receipt",
  ticketParseHint: "Photo or PDF of ticket",
  ticketParseHintBus: "Photo or PDF of bus ticket",
  ticketParseHintTrain: "Photo or PDF of train ticket",
  ticketParseHintFerry: "Photo or PDF of ferry ticket",
  ticketParsed: "Ticket data filled in",
  ticketParseFailed: "Could not parse ticket",
  boardingPassSegmentsTitle: "Multiple segments",
  boardingPassAddAll: "Add all flights",
  boardingPassUseOne: "Fill form",
  boardingPassSegmentsHint: "Total price split evenly across segments",
  boardingPassAllAdded: "All segments added",
  autoFill: "Auto-fill",
  getFlightData: "Get data",
  next: "Next",
  back: "Back",
  fuelAmount: "Fuel Amount (l)",
  fuelPrice: "Fuel Price ($/l)",
  tollRoadCost: "Toll roads ($)",
  noTollRoads: "No toll road segments",
  avoidFerries: "Avoid ferries",
  avoidTollRoads: "Avoid toll roads",
  buildingRoute: "Building route…",
  accommodationName: "Accommodation (name)",
  accommodationCost: "Accommodation cost",
  hotelName: "Hotel name",
  add: "Add segment",
  remove: "Remove",
  cost: "Cost ($)",
  flightPrice: "Flight cost",
  required: "*",
  selectType: "Select type",
  tag: "Journey",
  flightClass: "Flight class",
  economy: "Economy",
  premiumEconomy: "Premium Economy",
  business: "Business",
  first: "First",
  flightLabel: "Flight",
  timeLabel: "Time",
  timeInFlight: "Time in flight",
  internationalFlight: "International",
  domesticFlight: "Domestic",
  flightScope: "Flight type",

  // Helper texts
  distanceAutoCalculated: "Distance is automatically calculated using MapQuest",
  timeAutoCalculated: "Travel time is automatically calculated using MapQuest",
  
  // Modal titles
  addNewFlight: "Add New Flight",
  addNewTrip: "Add New Car Trip",
  tripDetails: "Trip Details",
  route: "Route",
  tripInfo: "Trip Information",
  totalCost: "Total Cost",
  
  // Other
  mapInDevelopment: "Route map is under development",
  inDevelopment: "Coming soon",
  addFirstFlight: "Add your first flight",
  mapModeAviation: "Flights",
  mapModeCar: "Car",
  mapAllYears: "All years",
  mapNoFlights: "No flights to display",
  mapSkippedCoords: "Missing coordinates: {n}",
  mapSelectYear: "Select year",
  trainDetails: "Train details",
  trainStops: "Stops",
  trainCarrier: "Carrier",
  trainBrandedName: "Train type",
  noTrainStops: "No stops found",

  planningNewTrip: "New trip",
  planningHistory: "Plan history",
  planningDestination: "Destination",
  planningMustSee: "Must-see places",
  planningInterests: "Interests",
  planningTransport: "Transport",
  planningTravelers: "Travelers",
  planningBudgetMin: "Budget from",
  planningBudgetMax: "Budget to",
  planningBudget: "Budget",
  planningPace: "Trip pace",
  planningAccommodation: "Accommodation",
  planningWithChildren: "Traveling with children",
  planningAccessibility: "Accessibility / dietary",
  planningAdditionalNotes: "Additional notes",
  planningDatesFlexible: "Flexible dates",
  planningGetSuggestions: "Get suggestions",
  planningGenerating: "Generating plan…",
  planningResults: "Trip options",
  planningNoResults: "No options",
  planningNoHistory: "No saved plans yet",
  planningVariants: "opt.",
  planningDeleteConfirm: "Delete this plan?",
  planningItinerary: "Day-by-day itinerary",
  planningDay: "Day",
  planningTransportTips: "Transport tips",
  planningInterestCulture: "Culture",
  planningInterestNature: "Nature",
  planningInterestFood: "Food",
  planningInterestNightlife: "Nightlife",
  planningInterestMuseums: "Museums",
  planningInterestBeaches: "Beaches",
  planningInterestHiking: "Hiking",
  planningInterestShopping: "Shopping",
  planningInterestArchitecture: "Architecture",
  planningInterestLocalLife: "Local life",
  planningInterestPhoto: "Photo",
  planningInterestSport: "Sport",
  planningTransportFlight: "Flight",
  planningTransportTrain: "Train",
  planningTransportCar: "Car",
  planningTransportBus: "Bus",
  planningTransportFerry: "Ferry",
  planningTransportMixed: "Mixed",
  planningTransportAny: "Any",
  planningPaceRelaxed: "Relaxed",
  planningPaceModerate: "Moderate",
  planningPaceActive: "Active",
  planningPaceIntensive: "Intensive",
  planningAccommodationHotel: "Hotel",
  planningAccommodationHostel: "Hostel",
  planningAccommodationApartment: "Apartment",
  planningAccommodationCamping: "Camping",
  planningAccommodationAny: "Any",
  planningTravelStyle: "Travel style",
  planningStyleDownshifter: "Backpacker",
  planningStyleBackpacker: "Backpacker",
  planningStyleBudget: "Budget",
  planningStyleMidrange: "Comfort",
  planningStyleElite: "Elite",
  planningStyleAdventure: "Adventure",
  planningStyleWellness: "Wellness",
  planningStyleFamily: "Family",
  planningStyleRomantic: "Romantic",
  planningStyleNomad: "Nomad",
  planningStyleDownshifterHint: "Hostels, local, cheap",
  planningStyleBackpackerHint: "Hostels, local, cheap",
  planningStyleBudgetHint: "Save without extremes",
  planningStyleMidrangeHint: "Balance of price & comfort",
  planningStyleEliteHint: "Luxury, service, premium",
  planningStyleAdventureHint: "Active, nature, challenge",
  planningStyleWellnessHint: "Spa, quiet, recovery",
  planningStyleFamilyHint: "Kid-friendly & safe",
  planningStyleRomanticHint: "For two, atmosphere",
  planningStyleNomadHint: "Wi‑Fi, coworking, flexible",
  planningHotels: "Hotels",
  planningUsefulLinks: "Useful links",
  planningCostBreakdown: "Cost breakdown",
  planningFoodTips: "Food",
  planningPracticalTips: "Practical",
  planningOpenLink: "Open",
  planningProTips: "Pro tips",
  planningCountryInfo: "Fact sheet",
  planningAboutCountry: "About the country",
  planningAboutRegion: "About the region",
  planningAboutRegions: "About the regions",
  planningRegionClimate: "Climate",
  planningRegionFood: "Regional food",
  planningRegionHighlights: "Highlights",
  planningSafety: "Safety",
  planningLifeExpectancy: "Life expectancy",
  planningPopulation: "Population",
  planningCapital: "Capital",
  planningPeaceIndex: "Global Peace Index (GPI)",
  planningInterestingFacts: "10 interesting facts",
  planningConnectivity: "Connectivity / SIM",
  planningCurrency: "Currency",
  planningPlugs: "Plugs",
  planningLanguage: "Language",
  planningVisa: "Visa",
  planningVisaEmbassy: "Embassy / consulate",
  planningHealth: "Health",
  planningUsefulPhrases: "Phrasebook",
  planningPhrasebook: "Phrasebook",
  planningGroceryCosts: "Average grocery costs",
  planningFuelPrice: "Fuel price",
  planningLodging: "Lodging",
  planningCamping: "Camping",
  planningHostel: "Hostel",
  planningApartment: "Apartment",
  planningSouvenirs: "Souvenirs",
  planningDuration3d: "3 days",
  planningDuration2d: "2 days",
  planningDuration1w: "1 week",
  planningDuration2w: "2 weeks",
  planningDuration1m: "1 month",
  planningTabOverview: "Overview",
  planningTabDays: "Days",
  planningDriveKm: "Distance today",
  planningOpenMap: "Map",
  planningHowToGet: "How to get there",
  planningTabRoad: "Where to go",
  planningTabCountries: "Countries",
  planningNoRoadStops: "No road stops yet — pick car/mixed transport and generate again",
  planningVisited: "Visited",
  planningMarkVisited: "Mark visited",
  planningUnmarkVisited: "Unmark",
  planningVisitedHint: "Marked places are saved and excluded on the next generation",
  planningExcludePlaces: "Already visited — exclude",
  planningExcludePlacesHint: "These places will be skipped in the new plan",
  planningAddExclude: "Add",
  planningStopTourist: "Tourist",
  planningStopLocal: "Local",
  planningStopViewpoint: "Viewpoint",
  planningStopFood: "Food",
  planningStopNature: "Nature",
  planningStopTown: "Town",
  planningStopOther: "Place",
  planningRoadConditions: "Road conditions",
  planningFuelNotes: "Fuel along the way",
  planningScenicHighlights: "Scenic highlights",
  planningRoadTips: "Driving tips",
  planningStopBestTime: "Best time",
  planningStopParking: "Parking",
  planningStopFoodNearby: "Food nearby",
  planningStopPhoto: "Photo tip",
  planningFree: "free",
  planningHistoryToggle: "Plan history",
  planningModeGeneral: "Trip plan",
  planningModeRoad: "Road trip",
  planningModeRoadHint: "Start → end: day-by-day drive with stops",
  planningModeWeekend: "Weekend",
  planningModeWeekendHint: "Home + weekend base, then return home",
  planningWeekendBase: "Weekend base",
  planningWeekendHome: "Home (return)",
  planningWeekendBaseHint: "e.g. Saint Petersburg",
  planningWeekendHomeHint: "Where you leave from and return to",
  planningRoadStart: "Start",
  planningRoadEnd: "End",
  planningRoadStartHint: "e.g. Kirov",
  planningRoadEndHint: "e.g. Nizhny Novgorod",
  planningMultiCountry: "Multi-country",
  planningDestinationHint: "One country or a route: Italy → Slovenia → Croatia",
  planningAddDestination: "Add stop",
  planningAddCountry: "Add country",
  planningAddCity: "Add city",
  planningCountryName: "Country",
  planningCityName: "City",
  planningCities: "Cities",
  planningNextStop: "Next stop",
  planningFreeTextHint: "City, country or region — free text",
  planningFormHint: "Free text — the LLM understands your route",
  planningTripPurpose: "Trip purpose",
  planningPurposeLeisure: "Leisure",
  planningPurposeRomantic: "Romantic",
  planningPurposeBusiness: "Business",
  planningPurposeCultural: "Cultural",
  planningSeason: "Season",
  planningSeasonAny: "Any",
  planningSeasonSpring: "Spring",
  planningSeasonSummer: "Summer",
  planningSeasonAutumn: "Autumn",
  planningSeasonWinter: "Winter",

  journeysSummary: "Diary",
  journeysAdd: "Add journey",
  journeysAddTitle: "New journey",
  journeysName: "Name",
  journeysDateFrom: "Start date",
  journeysDateTo: "End date",
  journeysDateUnknown: "Unknown",
  journeysEditDates: "Edit trip dates",
  journeysDateOpenEnded: "Open-ended",
  journeysCreated: "Journey created",
  journeysNoTags: "No journeys yet. Click «Add journey» or add a Journey to a flight, car, bus, or train trip.",
  journeysFlights: "flights",
  journeysCarTrips: "trips",
  journeysDiary: "Diary",
  journeysDayEntry: "What happened this day",
  journeysSaveDay: "Save",
  journeysVoiceStart: "Dictate",
  journeysVoiceStop: "Stop",
  journeysVoiceUnsupported: "Voice input is not supported in this browser",
  journeysBackToList: "All journeys",
  journeysRenameTag: "Rename tag",
  journeysRenameTagPlaceholder: "New name",
  journeysUntagged: "Untagged",
  journeysPickTrip: "Pick a journey",
  journeysEmptyDiary: "No entries yet — write your first day",
  journeysListening: "Listening…",
  journeysReport: "Report",
  journeysReportGenerating: "Writing report…",
  journeysReportTitle: "Trip report",
  journeysAddPhoto: "Add photo",
  journeysDeleteReport: "Delete report",
  journeysReportPdf: "PDF",
  journeysAlbum: "Album",
  journeysSetCover: "Cover",
  journeysCover: "Album cover",
  journeysExpenses: "Expenses",
  journeysExpenseTitle: "What for",
  journeysExpenseAmount: "Amount",
  journeysExpenseAdd: "Add",
  journeysExpenseSave: "Save",
  journeysExpenseCancel: "Cancel",
  journeysExpenseEdit: "Edit",
  journeysExpenseEmpty: "No expenses yet",
  journeysExpenseCategory: "Category",
  journeysCatFood: "Food",
  journeysCatTransport: "Transport",
  journeysCatStay: "Stay",
  journeysCatTickets: "Tickets",
  journeysCatSouvenirs: "Souvenirs",
  journeysCatOther: "Other",
  journeysSelectPhotos: "Select photos",
  journeysWriteHint: "Write what you saw and felt — or dictate with your voice",
  journeysEditingDay: "Editing day",
  journeysNewDay: "New entry",
  journeysDaysShort: "days",
  journeysPhotosShort: "photos",
  journeysOpenTrip: "Open",
  journeysEntrySaved: "Saved",
  journeysPlaces: "Places",
  journeysPlacesHint: "Trip checklist — tick places you’ve already visited",
  journeysPlaceName: "Place name",
  journeysPlaceAdd: "Add",
  journeysPlacesEmpty: "No places yet — add the first one",
  journeysPlacesShowAll: "All days",
  journeysPlaceVisit: "Mark as visited",
  journeysPlaceUnvisit: "Unmark",
  journeysDayPlaces: "Places for this day",
  journeysInsertPlaces: "Insert into entry",
  journeysPlaceDate: "Date",
  journeysDayPlacesEmpty: "No places for this day",
  journeysVoiceHint: "Dictation works in Chrome / Edge over HTTPS. Use the mic under the text.",
  journeysMapViewList: "List",
  journeysMapViewMap: "Map",
  journeysMapAllTags: "All trips",
  journeysMapTagFilter: "Journey",
  journeysMapEmpty: "No geotagged photos yet. Open a trip and use “On map” on a photo, or upload a GPS shot.",
  journeysMapPlaceOnMap: "On map",
  journeysMapTapHint: "Tap the map to place a pin",
  journeysMapClearLocation: "Clear",
  journeysMapPickPoint: "Pick a point on the map first",
  journeysGlobeViewDiary: "Diary",
  journeysGlobeViewGlobe: "Globe",
  journeysGlobeNoToken: "Mapbox token is not configured in Vault (key mytraveldiary_mapbox_token).",
  journeysGlobeEmpty: "No routes with coordinates or geotagged photos for this trip yet.",
  journeysSegmentDetails: "Trip details",
  journeysSegmentFrom: "From",
  journeysSegmentTo: "To",
  journeysShare: "Share",
  journeysShareEnable: "Enable link",
  journeysShareDisable: "Disable",
  journeysShareCopy: "Copy link",
  journeysShareCopied: "Link copied",
  journeysShareRotate: "New link",
  journeysShareHint: "The link shows diary, photos and map",
  journeysShareNotFound: "Link unavailable",
  journeysShareNotFoundHint: "The link was disabled or is invalid",
  journeysExportPdf: "Trip PDF",
  journeysUploadCover: "Upload cover",
  journeysCoverHint: "Upload a cover separately or pick ★ from the album",
  journeysCurrencyCustom: "Other",
  journeysImportExpenses: "From transport / hotels",
  journeysImportExpensesHint: "Pick priced items with this tag",
  journeysImportExpensesEmpty: "No unimported priced items",
  journeysImportSelected: "Add selected",
  hotels: "Hotels",
  hotelsAdd: "Add hotel",
  hotelsEdit: "Edit hotel",
  hotelsEmpty: "No hotels yet — add a booking via photo or link",
  carsEmpty: "No car trips yet — add a route",
  busesEmpty: "No bus trips yet — add a ticket via photo or PDF",
  trainsEmpty: "No train trips yet — add a ticket via photo or PDF",
  ferriesEmpty: "No ferry trips yet — add a ticket via photo or PDF",
  hotelsParseHint: "Parse booking: photo/PDF or URL",
  hotelsParsePhoto: "Booking photo",
  hotelsParseUrl: "From URL",
  hotelsName: "Name",
  hotelsCity: "City",
  hotelsCountry: "Country",
  hotelsCheckIn: "Check-in",
  hotelsCheckOut: "Check-out",
  hotelsConfirmation: "Confirmation #",
  hotelsUrl: "Booking URL",
  journeysPlacesValidate: "Validate",
  journeysPlacesValidating: "Validating…",
  journeysPlacesValidated: "Names updated",
  journeysPlacesFromPlan: "From plan",
  journeysPlacesImporting: "Importing…",
  journeysPlacesImported: "Places imported from plan",
  journeysPlacesPickPlan: "Choose a saved plan",
  journeysPlaceInfo: "Place details",
  journeysPlaceInfoLoading: "Generating description…",
  journeysPlaceInfoUnavailable: "Description unavailable",
  registerCheckEmail: "Account created. Confirm your email via the link we sent, then sign in.",

  tagRequired: "Journey is required",
  tagSelectOrCreate: "Journey",

  // Pagination
  previousPage: "Previous page",
  nextPage: "Next page",
  page: "Page",
  of: "of",

  adminTitle: "Admin",
  adminSubtitle: "Users and login attempts",
  adminRefresh: "Refresh",
  adminUsers: "Users",
  adminLoginEvents: "Login attempts",
  adminColStatus: "Status",
  adminColRole: "Role",
  adminColCreated: "Created",
  adminColTime: "Time",
  adminColResult: "Result",
  adminColReason: "Reason",
  adminActive: "Active",
  adminInactive: "Disabled",
  adminUnverified: "Unverified",
  adminRoleAdmin: "Admin",
  adminRoleUser: "User",
  adminLoginOk: "Success",
  adminLoginFail: "Failed",
  adminEmptyUsers: "No users yet",
  adminEmptyEvents: "No login events yet — they appear after sign-ins",
  adminClearEvents: "Clear",
  adminClearEventsConfirm: "Delete all login attempt records?",
  adminReasonPasswordOk: "Password",
  adminReasonGoogleOk: "Google",
  adminReasonBadPassword: "Bad password",
  adminReasonUserNotFound: "User not found",
  adminReasonRateLimited: "Rate limited",
  adminReasonNoPassword: "No password",
  adminReasonWrongProvider: "Wrong provider",
  adminReasonEmailUnverified: "Email unverified",
  adminCountries: "Countries",
  adminTabUsers: "Users",
  adminTabCountries: "Countries",
  adminTabSubdivisions: "Regions",
  adminAddCountry: "Add country",
  adminAddSubdivision: "Add region",
  adminEditSubdivision: "Edit region",
  adminDeleteSubdivisionConfirm: "Delete this region?",
  adminEmptySubdivisions: "No regions yet",
  adminAllCountries: "All countries",
  adminColCountry: "Country",
  adminColType: "Type",
  adminEditCountry: "Edit country",
  adminDeleteCountry: "Delete country",
  adminDeleteCountryConfirm: "Delete this country?",
  adminColIso2: "ISO2",
  adminColNameRu: "Name (RU)",
  adminColNameEn: "Name (EN)",
  adminColRegion: "Region",
  adminColFlag: "Flag",
  adminColGroup: "Group",
  adminColSecondaryFlag: "Secondary flag",
  adminEmptyCountries: "No countries yet",
  adminSaveCountry: "Save country",
  adminFlagUrl: "Flag URL",
  adminFlagCode: "Flag code",
  adminTerritoryGroup: "Territory group",
  adminSecondaryFlagUrl: "Secondary flag URL",
  adminSearchCountries: "Search by ISO2 or name…",
  adminClearFlagUrl: "Clear custom flag URL",
  adminClearSecondaryFlagUrl: "Clear secondary flag URL",

  // Gas station related
  gasStations: "Gas Stations",
  gasStation: "Gas Station",
  addGasStation: "Add Gas Station",
  removeGasStation: "Remove Gas Station",
  noGasStations: "No gas stations recorded for this trip.",
  gasStationAndFuel: "Gas Station & Fuel",
  totalFuelFilled: "Total Fuel Filled",
  location: "Fuel cost",
  enterLocation: "Enter fuel cost",
  fuelCost: "Fuel Price ($/l)",
  enterFuelCost: "Enter fuel price",
  optional: "optional",
  enterFuelAmount: "Enter fuel amount",
  loadingMap: "Loading map..."
} satisfies TranslationKeys;
